import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { X, Check } from 'lucide-react';
import '../styles/KatKarsilig.css';

interface Arsa {
    arsaID: number;
    takipNo: string;
    ada: string | null;
    parsel: string | null;
    il: string | null;
    ilce: string | null;
    mahalle: string | null;
    arsaMetrekare: number | null;
    aciklama: string | null;
    olusturmaTarihi: string;
    aktifMi: boolean;
    muteahhitSayisi: number;
    ortakSayisi: number;
    bolumSayisi: number;
}

interface ArsaForm {
    takipNo: string;
    ada: string;
    parsel: string;
    il: string;
    ilce: string;
    mahalle: string;
    arsaMetrekare: string;
    aciklama: string;
    aktifMi: boolean;
}

const emptyForm: ArsaForm = {
    takipNo: '',
    ada: '',
    parsel: '',
    il: '',
    ilce: '',
    mahalle: '',
    arsaMetrekare: '',
    aciklama: '',
    aktifMi: true,
};


const KatKarsiligArsa: React.FC = () => {
    const navigate = useNavigate();
    const [arsalar, setArsalar] = useState<Arsa[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [form, setForm] = useState<ArsaForm>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchArsalar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/KatKarsiligArsa');
            setArsalar(res.data);
        } catch {
            setError('Veriler yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchArsalar();
    }, [fetchArsalar]);

    const openCreate = () => {
        setEditId(null);
        setForm(emptyForm);
        setError('');
        setShowModal(true);
    };

    const openEdit = (a: Arsa) => {
        setEditId(a.arsaID);
        setForm({
            takipNo: a.takipNo,
            ada: a.ada ?? '',
            parsel: a.parsel ?? '',
            il: a.il ?? '',
            ilce: a.ilce ?? '',
            mahalle: a.mahalle ?? '',
            arsaMetrekare: a.arsaMetrekare !== null ? String(a.arsaMetrekare) : '',
            aciklama: a.aciklama ?? '',
            aktifMi: a.aktifMi,
        });
        setError('');
        setShowModal(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                arsaMetrekare: form.arsaMetrekare !== '' ? parseFloat(form.arsaMetrekare) : null,
            };
            if (editId !== null) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${editId}`, payload);
            } else {
                await axiosInstance.post('/api/KatKarsiligArsa', payload);
            }
            setShowModal(false);
            fetchArsalar();
        } catch (err: any) {
            setError(err?.response?.data || 'Kayıt başarısız.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bu arsayı silmek istediğinizden emin misiniz?')) return;
        try {
            await axiosInstance.delete(`/api/KatKarsiligArsa/${id}`);
            fetchArsalar();
        } catch {
            alert('Silme işlemi başarısız.');
        }
    };

    const filtered = arsalar.filter(a => {
        const q = search.toLowerCase();
        return (
            a.takipNo.toLowerCase().includes(q) ||
            (a.ada ?? '').toLowerCase().includes(q) ||
            (a.parsel ?? '').toLowerCase().includes(q) ||
            (a.il ?? '').toLowerCase().includes(q) ||
            (a.ilce ?? '').toLowerCase().includes(q) ||
            (a.mahalle ?? '').toLowerCase().includes(q)
        );
    });

    return (
        <div className="kka-page">
            <div className="kka-header">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Kat Karşılığı</h1>
                    <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>Kat karşılığı arsa portföyünüzü ve anlaşma detaylarını yönetin.</p>
                </div>
                <button className="kka-btn-primary" onClick={openCreate}>
                    <i className="fas fa-plus" style={{ marginRight: 6 }}></i>Yeni Arsa
                </button>
            </div>

            <div className="kka-search-row">
                <input
                    className="kka-search-input"
                    placeholder="Takip No, Ada, Parsel, İl, İlçe, Mahalle..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {loading ? (
                <div className="kka-loading">Yükleniyor...</div>
            ) : (
                <div className="kka-table-wrap">
                    <table className="kka-table">
                        <thead>
                            <tr>
                                <th>Takip No</th>
                                <th>Ada / Parsel</th>
                                <th>Konum</th>
                                <th style={{ textAlign: 'right' }}>Alan (m²)</th>
                                <th style={{ textAlign: 'center' }}>Müteahhit</th>
                                <th style={{ textAlign: 'center' }}>Ortak</th>
                                <th style={{ textAlign: 'center' }}>Bölüm</th>
                                <th>Durum</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="kka-empty">Kayıt bulunamadı.</td>
                                </tr>
                            ) : filtered.map(a => (
                                <tr key={a.arsaID}>
                                    <td><strong>{a.takipNo}</strong></td>
                                    <td>{[a.ada, a.parsel].filter(Boolean).join(' / ') || '–'}</td>
                                    <td>{[a.il, a.ilce, a.mahalle].filter(Boolean).join(', ') || '–'}</td>
                                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{a.arsaMetrekare !== null ? `${a.arsaMetrekare.toLocaleString('tr-TR')} m²` : '–'}</td>
                                    <td style={{ textAlign: 'center' }}><span className="kka-count-chip">{a.muteahhitSayisi}</span></td>
                                    <td style={{ textAlign: 'center' }}><span className="kka-count-chip">{a.ortakSayisi}</span></td>
                                    <td style={{ textAlign: 'center' }}><span className="kka-count-chip">{a.bolumSayisi}</span></td>
                                    <td>
                                        <span className={`kka-status-badge ${a.aktifMi ? 'aktif' : 'pasif'}`}>
                                            {a.aktifMi ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="kka-action-cell">
                                            <button
                                                className="kka-btn-detail"
                                                onClick={() => navigate(`/kat-karsiligi/${a.arsaID}`)}
                                            >
                                                Detay
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {showModal && (() => {
                const inp: React.CSSProperties = {
                    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
                    borderRadius: 8, fontSize: 13, color: '#111827', outline: 'none',
                    background: '#fff', boxSizing: 'border-box',
                };
                const lbl: React.CSSProperties = {
                    display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4,
                };
                return (
                    <div
                        onClick={() => setShowModal(false)}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 1050,
                            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                        }}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{
                                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 768,
                                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                            }}
                        >
                            {/* Header */}
                            <div style={{
                                padding: '20px 28px 16px', borderBottom: '1px solid #e5e7eb',
                                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0,
                            }}>
                                <div>
                                    <div style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>
                                        {editId !== null ? 'Arsayı Düzenle' : 'Yeni Arsa Ekle'}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                                        Kat karşılığı projeleriniz için arsa detaylarını sisteme girin.
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                                        padding: 4, borderRadius: 8, display: 'flex', alignItems: 'center',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.color = '#4b5563')}
                                    onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Body */}
                            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                                {error && (
                                    <div style={{ color: '#dc2626', fontSize: 13, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                                        {error}
                                    </div>
                                )}

                                {/* Takip No (edit mode only) */}
                                {editId !== null && (
                                    <div>
                                        <label style={lbl}>Takip No</label>
                                        <input value={form.takipNo} readOnly style={{ ...inp, background: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed' }} />
                                    </div>
                                )}

                                {/* Tapu Bilgileri: Ada, Parsel, Alan */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={lbl}>Ada</label>
                                        <input style={inp} value={form.ada}
                                            onChange={e => setForm(f => ({ ...f, ada: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Parsel</label>
                                        <input style={inp} value={form.parsel}
                                            onChange={e => setForm(f => ({ ...f, parsel: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Alan (m²)</label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="number"
                                                style={{ ...inp, paddingRight: 36 }}
                                                value={form.arsaMetrekare}
                                                onChange={e => setForm(f => ({ ...f, arsaMetrekare: e.target.value }))}
                                            />
                                            <span style={{
                                                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                                fontSize: 12, color: '#9ca3af', pointerEvents: 'none',
                                            }}>m²</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Konum: İl, İlçe, Mahalle */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={lbl}>İl</label>
                                        <input style={inp} value={form.il}
                                            onChange={e => setForm(f => ({ ...f, il: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label style={lbl}>İlçe</label>
                                        <input style={inp} value={form.ilce}
                                            onChange={e => setForm(f => ({ ...f, ilce: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Mahalle</label>
                                        <input style={inp} value={form.mahalle}
                                            onChange={e => setForm(f => ({ ...f, mahalle: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Açıklama */}
                                <div>
                                    <label style={lbl}>Açıklama</label>
                                    <textarea
                                        rows={3}
                                        style={{ ...inp, resize: 'vertical', minHeight: 80 }}
                                        value={form.aciklama}
                                        onChange={e => setForm(f => ({ ...f, aciklama: e.target.value }))}
                                    />
                                </div>

                                {/* Durum Toggle */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: 10,
                                    background: 'rgba(249,250,251,0.5)',
                                }}>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Durum</div>
                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
                                            {form.aktifMi ? 'Arsa aktif görünüyor' : 'Arsa pasif, listede görünmez'}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setForm(f => ({ ...f, aktifMi: !f.aktifMi }))}
                                        style={{
                                            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                                            background: form.aktifMi ? '#cd6906' : '#d1d5db',
                                            position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                                            padding: 0,
                                        }}
                                    >
                                        <span style={{
                                            position: 'absolute', top: 2,
                                            left: form.aktifMi ? 22 : 2,
                                            width: 20, height: 20, borderRadius: '50%',
                                            background: '#fff', transition: 'left 0.2s',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                        }} />
                                    </button>
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{
                                padding: '16px 28px', borderTop: '1px solid #e5e7eb', background: '#fff',
                                display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
                                borderRadius: '0 0 16px 16px',
                            }}>
                                <button
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db',
                                        background: '#fff', fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    style={{
                                        padding: '8px 20px', borderRadius: 8, border: 'none',
                                        background: saving ? '#e5e7eb' : '#cd6906',
                                        color: saving ? '#9ca3af' : '#fff',
                                        fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        boxShadow: saving ? 'none' : '0 1px 3px rgba(205,105,6,0.3)',
                                    }}
                                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                                    onMouseLeave={e => { if (!saving) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                                >
                                    {saving ? 'Kaydediliyor...' : <><Check size={14} /> Kaydet</>}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default KatKarsiligArsa;
